import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { OrderLike } from '@/types/order/order';
import type {
  TransitionEntityType,
  DeliveryPhotoItem,
  DeliveryDialogState,
  DrawerState,
  StatusDrawerState,
  AssignDrawerState,
  OrderPartsTransitionSheetState,
  OrderPartsTransitionMode,
  TransitionFlagRecord,
  OrderTransitionState,
} from '@/types/order/orderTransition';
import { IntakeGroup, NormalizedOrderItem } from '@/lib/orders/normalizeOrders';

const initialDeliveryDialogState = (): DeliveryDialogState => ({
  open: false,
  entityId: null,
  thirdParty: false,
  entityType: 'order',
  photos: [],
  pendingStatusId: null,
});

const initialDrawerState = (): DrawerState => ({
  open: false,
  entityId: null,
  entityType: 'order',
});

const initialStatusDrawerState = (): StatusDrawerState => ({
  ...initialDrawerState(),
  draftStatusId: null,
  draftStatusName: null,
  currentStatusId: null,
  currentStatusName: null,
});

const initialAssignDrawerState = (): AssignDrawerState => ({
  ...initialDrawerState(),
  propagate: false,
});

const initialPartsTransitionSheetState = (): OrderPartsTransitionSheetState => ({
  open: false,
  orderId: null,
  targetStatusId: null,
  targetStatusName: null,
  mode: null,
});

const initialOrderState = (): OrderLike => ({
  id: -1,
  order_code: undefined,
  order_status_id: undefined,
  order_status_name: undefined,
  paid_amount: undefined,
  total_cost: undefined,
  repair_cost: undefined,
  has_pending_serial_parts: false,
  pending_serial_count: 0,
  has_blocking_order_parts: false,
  blocking_order_parts_count: 0,
  requested_order_parts_count: 0,
  intake_id: undefined,
  intake_code: undefined,
  assigned_to_user_id: undefined,
  client_id: undefined,
  client_name: undefined,
  client_contact_number: undefined,
  equipment_type: undefined,
  equipment_model: undefined,
  equipment_brand: undefined,
  created_at: undefined,
});

const makeEntityKey = (entityType: TransitionEntityType, entityId: number | null) =>
  `${entityType}:${entityId ?? 'none'}`;

const initialState: OrderTransitionState = {
  partsTransitionSheet: initialPartsTransitionSheetState(),
  deliveryDialog: initialDeliveryDialogState(),
  statusDrawer: initialStatusDrawerState(),
  assignDrawer: initialAssignDrawerState(),
  assigning: {},
  changing: {},
  activeOrder: initialOrderState(),
};

const orderTransitionSlice = createSlice({
  name: 'orderTransition',
  initialState,
  reducers: {
    resetState: () => initialState,
    setActiveOrder(state, action: PayloadAction<OrderLike | null>) {
      state.activeOrder = action.payload ? { ...action.payload } : initialOrderState();
    },
    openPartsTransitionSheet(
      state,
      action: PayloadAction<{
        orderId: number;
        targetStatusId: number;
        targetStatusName: string | null;
        mode: OrderPartsTransitionMode;
      }>,
    ) {
      state.partsTransitionSheet = {
        open: true,
        orderId: action.payload.orderId,
        targetStatusId: action.payload.targetStatusId,
        targetStatusName: action.payload.targetStatusName,
        mode: action.payload.mode,
      };
    },
    closePartsTransitionSheet(state) {
      state.partsTransitionSheet = initialPartsTransitionSheetState();
    },
    openDeliveryDialog(
      state,
      action: PayloadAction<{ entityId: number; pendingStatusId: number | null }>,
    ) {
      state.deliveryDialog = initialDeliveryDialogState();
      state.deliveryDialog = {
        open: true,
        entityId: action.payload.entityId,
        entityType: 'order',
        pendingStatusId: action.payload.pendingStatusId ?? null,
        thirdParty: false,
        photos: [],
      };
    },
    closeDeliveryDialog(state) {
      state.deliveryDialog = initialDeliveryDialogState();
    },
    setDeliveryDialogThirdParty(state, action: PayloadAction<boolean>) {
      state.deliveryDialog.thirdParty = action.payload;
    },
    setDeliveryDialogPhotos(state, action: PayloadAction<DeliveryPhotoItem[]>) {
      state.deliveryDialog.photos = action.payload;
    },
    setDeliveryDialogPendingStatus(state, action: PayloadAction<number | null>) {
      state.deliveryDialog.pendingStatusId = action.payload ?? null;
    },
    openStatusDrawer(
      state,
      action: PayloadAction<{
        entityId: number;
        entityType?: TransitionEntityType;
        currentStatusId?: number | null;
        currentStatusName?: string | null;
        order?: OrderLike | null;
      }>,
    ) {
      // Limpiamos cualquier rastro previo antes de abrir el drawer nuevamente.
      state.statusDrawer = initialStatusDrawerState();
      state.statusDrawer.open = true;
      state.statusDrawer.entityId = action.payload.entityId;
      state.statusDrawer.entityType = action.payload.entityType ?? 'order';
      state.statusDrawer.currentStatusId = action.payload.currentStatusId ?? null;
      state.statusDrawer.currentStatusName = action.payload.currentStatusName ?? null;
      if (state.statusDrawer.entityType === 'order') {
        state.activeOrder = action.payload.order ?? initialOrderState();
      } else {
        state.activeOrder = initialOrderState();
      }
    },
    // Guarda temporalmente el estatus que el usuario seleccionó dentro del picker.
    setStatusDrawerDraft(
      state,
      action: PayloadAction<{ statusId: number | null; statusName: string | null }>,
    ) {
      state.statusDrawer.draftStatusId = action.payload.statusId ?? null;
      state.statusDrawer.draftStatusName = action.payload.statusName ?? null;
    },
    // Limpia ese borrador sin necesidad de cerrar todo el drawer.
    clearStatusDrawerDraft(state) {
      state.statusDrawer.draftStatusId = null;
      state.statusDrawer.draftStatusName = null;
    },
    closeStatusDrawer(state) {
      state.statusDrawer = initialStatusDrawerState();
      // if (!(state.assignDrawer.open && state.assignDrawer.entityType === 'order')) {
      //   state.activeOrder = null;
      // }
    },
    openAssignDrawer(
      state,
      action: PayloadAction<{
        entityId: number;
        entityType?: TransitionEntityType;
        propagate?: boolean;
        order?: OrderLike | null;
      }>,
    ) {
      state.assignDrawer = initialAssignDrawerState();
      state.assignDrawer.open = true;
      state.assignDrawer.entityId = action.payload.entityId;
      state.assignDrawer.entityType = action.payload.entityType ?? 'order';
      state.assignDrawer.propagate = Boolean(action.payload.propagate);
      if (state.assignDrawer.entityType === 'order') {
        state.activeOrder = action.payload.order ?? initialOrderState();
      } else if (!(state.statusDrawer.open && state.statusDrawer.entityType === 'order')) {
        state.activeOrder = initialOrderState();
      }
    },
    closeAssignDrawer(state) {
      state.assignDrawer = initialAssignDrawerState();
      if (!(state.statusDrawer.open && state.statusDrawer.entityType === 'order')) {
        state.activeOrder = initialOrderState();
      }
    },
    setAssignDrawerPropagate(state, action: PayloadAction<boolean>) {
      state.assignDrawer.propagate = action.payload;
    },
    setAssigningFlag(
      state,
      action: PayloadAction<{
        entityId: number | null;
        entityType?: TransitionEntityType;
        value: boolean;
      }>,
    ) {
      const entityType = action.payload.entityType ?? 'order';
      const key = makeEntityKey(entityType, action.payload.entityId ?? null);
      if (action.payload.value) {
        state.assigning[key] = true;
      } else {
        delete state.assigning[key];
      }
    },
    setChangingFlag(
      state,
      action: PayloadAction<{
        entityId: number | null;
        entityType?: TransitionEntityType;
        value: boolean;
      }>,
    ) {
      const entityType = action.payload.entityType ?? 'order';
      const key = makeEntityKey(entityType, action.payload.entityId ?? null);
      if (action.payload.value) {
        state.changing[key] = true;
      } else {
        delete state.changing[key];
      }
    },
  },
});

export const {
  resetState,
  setActiveOrder,
  openPartsTransitionSheet,
  closePartsTransitionSheet,
  openDeliveryDialog,
  closeDeliveryDialog,
  setDeliveryDialogThirdParty,
  setDeliveryDialogPhotos,
  setDeliveryDialogPendingStatus,
  openStatusDrawer,
  setStatusDrawerDraft,
  clearStatusDrawerDraft,
  closeStatusDrawer,
  openAssignDrawer,
  closeAssignDrawer,
  setAssignDrawerPropagate,
  setAssigningFlag,
  setChangingFlag,
} = orderTransitionSlice.actions;

export default orderTransitionSlice.reducer;
